import React, { useId, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Styled from './styled';

interface PopoverMenuLink {
  stringsId: string;
  href: string;
  type: 'link';
  dataGtm?: string;
}

interface PopoverMenuButton {
  stringsId: string;
  onSelect: () => void;
  type: 'button';
  dataGtm?: string;
}

type PopoverMenuListItem = PopoverMenuLink | PopoverMenuButton;

interface ItemsProps {
  menuItems: PopoverMenuListItem[];
}

function PopoverItems({ menuItems }: ItemsProps) {
  return (
    <Styled.PopOverMenuList id="popover-menu">
      {menuItems.map(item => (
        <Styled.PopoverMenuListItem key={item.stringsId}>
          {item.type === 'button' ? (
            <button
              type="button"
              onClick={item.onSelect}
              data-gtm={item.dataGtm}
            >
              <FormattedMessage id={item.stringsId} />
            </button>
          ) : (
            <a
              href={item.href}
              target="_blank"
              rel="noreferrer noopener"
              data-gtm={item.dataGtm}
            >
              <FormattedMessage id={item.stringsId} />
            </a>
          )}
        </Styled.PopoverMenuListItem>
      ))}
    </Styled.PopOverMenuList>
  );
}

function PopoverMenu({ menuItems }: ItemsProps) {
  const popoverMenuId = useId();
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const blurRef = useRef<HTMLDivElement>(null);
  const ellipsesButtonRef = useRef<HTMLButtonElement>(null);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  const handleBlur = (e: React.FocusEvent) => {
    const relatedTarget = e.relatedTarget as Node;
    if (blurRef.current && !blurRef.current.contains(relatedTarget)) {
      closeMenu();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      closeMenu();
      ellipsesButtonRef.current?.focus();
    }
  };

  return (
    <Styled.EllipsesButtonGroup
      ref={blurRef}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      <Styled.EllipsesButton
        ref={ellipsesButtonRef}
        aria-label={intl.formatMessage({
          id: 'detailview.ellipses-menu',
        })}
        type="button"
        aria-expanded={isOpen}
        aria-controls={popoverMenuId}
        onClick={toggleMenu}
      >
        <Styled.EllipsesMenuIcon aria-hidden />
      </Styled.EllipsesButton>

      {isOpen && (
        <Styled.PopOver id={popoverMenuId}>
          <PopoverItems menuItems={menuItems} />
        </Styled.PopOver>
      )}
    </Styled.EllipsesButtonGroup>
  );
}

export default PopoverMenu;
